import React, { useEffect, useState } from "react";

import { AptihealthIcon } from "@/component-library/Icons/components/AptihealthIcon";
import { BhsIcon } from "@/component-library/Icons/components/BhsIcon";
import { BuildingIcon } from "@/component-library/Icons/components/BuildingIcon";
import { CaseManagerIcon } from "@/component-library/Icons/components/CaseManagerIcon";
import { IntakeIcon } from "@/component-library/Icons/components/IntakeIcon";
import { PcpIcon } from "@/component-library/Icons/components/PcpIcon";
import { PeerIcon } from "@/component-library/Icons/components/PeerIcon";
import { PrescriberIcon } from "@/component-library/Icons/components/PrescriberIcon";
import { ICON_SIZES, ICON_TYPES } from "@/constants/icons";
import images from "@/utils/images";

import "../styles.scss";

const CareTeamWidget = (props) => {
  const { profile, sendBirdContacts } = props;
  const [careTeamMembers, setCareTeamMembers] = useState(null);

  useEffect(() => {
    const careTeam =
      profile && profile.care_team_members
        ? [...profile.care_team_members]
        : [{ type: "No Members" }];

    careTeam.push({ type: "Member Support" });
    setCareTeamMembers(careTeam);
  }, [profile]);

  const ACTION_CONFIG = {
    Email: {
      clickHandler: () => openEmail(),
      icon: "./icons/common/email/email-ter-white.svg",
      name: "Email",
    },
    Message: {
      clickHandler: (memberId) => openMessage(memberId),
      icon: "./icons/common/message/message-ter-none-2.svg",
      name: "Message",
    },
  };

  const CARE_TEAM_MEMBER_ROLE_CONFIG = {
    "Behavioral Specialist": {
      action: ACTION_CONFIG["Message"],
      description: (
        <>
          Your licensed <b>therapist</b> will meet with you regularly for video therapy to help you
          set and track your goals. They are also available for support between sessions using our
          in-app messaging.
        </>
      ),
      icon: (
        <BhsIcon
          className="IconAptiNavy"
          iconSize={ICON_SIZES.LARGE}
          iconType={ICON_TYPES.CIRCLE}
        />
      ),
      title: "Therapist",
    },
    "Care Manager": {
      description: (
        <>
          Your primary care physician’s in-office <b>care manager</b> will support your physical and
          behavioral healthcare needs.
        </>
      ),
      icon: (
        <BuildingIcon
          className="IconAptiNavy"
          iconSize={ICON_SIZES.LARGE}
          iconType={ICON_TYPES.CIRCLE}
        />
      ),
      title: "Care Manager",
    },
    "Case Manager": {
      action: ACTION_CONFIG["Message"],
      description: (
        <>
          Your <b>case manager</b> will work with you to develop and coordinate your care plan,
          ensuring you have access to resources and support. They’ll check in to assess your
          progress and adjust your plan as needed.
        </>
      ),
      icon: (
        <CaseManagerIcon
          className="IconAptiNavy"
          iconSize={ICON_SIZES.LARGE}
          iconType={ICON_TYPES.CIRCLE}
        />
      ),
      title: "Case Manager",
    },
    "Intake Behavioral Specialist": {
      action: ACTION_CONFIG["Message"],
      description: (
        <>
          Your <b>intake coordinator</b> will meet with you for your first session to review your
          goals for your first 90 days of therapy. They’ll match you with a licensed therapist and a
          licensed prescriber, if needed.
        </>
      ),
      icon: (
        <IntakeIcon
          className="IconAptiNavy"
          iconSize={ICON_SIZES.LARGE}
          iconType={ICON_TYPES.CIRCLE}
        />
      ),
      title: "Intake Coordinator",
    },
    "Member Support": {
      action: ACTION_CONFIG["Email"],
      description: (
        <>
          aptihealth’s <b>member support</b> team is available by email to assist with questions you
          have throughout your care journey. Dedicated to providing the support you need, they’ll
          respond to your emails promptly.
        </>
      ),
      icon: (
        <AptihealthIcon
          className="IconAptiNavy"
          iconSize={ICON_SIZES.LARGE}
          iconType={ICON_TYPES.CIRCLE}
        />
      ),
      title: "Member Support",
    },
    "No Members": {
      description: (
        <>
          Members of your personalized care team, including a licensed therapist and a licensed
          prescriber, if needed, will appear here.
        </>
      ),
      icon: (
        <AptihealthIcon
          className="IconAptiNavy"
          iconSize={ICON_SIZES.LARGE}
          iconType={ICON_TYPES.CIRCLE}
        />
      ),
      title: "You Are Being Matched to Your Care Team",
    },
    PCP: {
      description: (
        <>
          Your <b>primary care physician</b> will collaborate closely with your therapist and
          prescriber. Your provider has access to your assessments and care notes so the physical
          care they provide supports your behavioral healthcare.
        </>
      ),
      icon: (
        <PcpIcon
          className="IconAptiNavy"
          iconSize={ICON_SIZES.LARGE}
          iconType={ICON_TYPES.CIRCLE}
        />
      ),
      title: "Primary Care Physician",
    },
    Peer: {
      action: ACTION_CONFIG["Message"],
      description: (
        <>
          Your <b>peer specialist</b> offers understanding and practical advice based on their own
          experience. They’ll contact you regularly to provide encouragement, check in on progress,
          and help problem-solve.
        </>
      ),
      icon: (
        <PeerIcon
          className="IconAptiNavy"
          iconSize={ICON_SIZES.LARGE}
          iconType={ICON_TYPES.CIRCLE}
        />
      ),
      title: "Peer",
    },
    Prescriber: {
      action: ACTION_CONFIG["Message"],
      description: (
        <>
          Your licensed <b>prescriber</b> will meet with you regularly to help manage your
          medication and adjust your prescriptions to ensure you’re meeting your care goals.
        </>
      ),
      icon: (
        <PrescriberIcon
          className="IconAptiNavy"
          iconSize={ICON_SIZES.LARGE}
          iconType={ICON_TYPES.CIRCLE}
        />
      ),
      title: "Prescriber",
    },
  };

  const openMessage = (memberId) => {
    props.history.push(`/app/messages?memberId=${memberId}`);
  };

  const openEmail = () => {
    window.location.href = "mailto:support@aptihealth.com?subject=Web Support Request";
  };

  const allowSendMessage = (memberConfig, sendBirdContacts, member) => {
    return (
      memberConfig["action"] &&
      (memberConfig["title"] !== "Prescriber" ||
        (sendBirdContacts[member["provider_id"]] &&
          sendBirdContacts[member["provider_id"]]["shouldShow"]))
    );
  };

  const buildCareTeamMemberDescription = (member) => {
    const memberConfig =
      member && member["type"] && member["type"] !== "Intake Behavioral Specialist"
        ? CARE_TEAM_MEMBER_ROLE_CONFIG[member["type"]]
        : null;

    if (memberConfig) {
      return (
        <div
          className="col-lg-4 col-md-6 col-12"
          key={`care-team-member-${member["type"]}-${member["name"]}`}
        >
          <div className="d-flex p-2 mb-2">
            <div className="px-2">{memberConfig["icon"]}</div>
            <div className="px-2">
              <div className="txt-ter tw:font-bold">
                {member["name"]
                  ? `${memberConfig["title"]}, ${member["name"]}`
                  : memberConfig["title"]}
              </div>
              <div className="fs-14 txt-gry">{memberConfig["description"]}</div>
              {allowSendMessage(memberConfig, sendBirdContacts, member) && (
                <div
                  className="txt-ter fs-14 d-flex cursor tw:font-bold"
                  onClick={() => memberConfig["action"]["clickHandler"](member["provider_id"])}
                  role="button"
                >
                  <img alt="" src={images(memberConfig["action"]["icon"])} />
                  <div className="mt-1 ml-1">{memberConfig["action"]["name"]}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    } else {
      return <></>;
    }
  };

  return (
    <div className="mb-3 pb-md-3 pb-0">
      <div className="fs-22 txt-gry pb-2 tw:font-bold">Care Team</div>
      <div className="py-3 dashboard-widget-card dashboard-widget-card-xl">
        <div className="row">
          {profile &&
            careTeamMembers &&
            sendBirdContacts &&
            careTeamMembers.map(buildCareTeamMemberDescription)}
        </div>
      </div>
    </div>
  );
};

export default CareTeamWidget;
